<template>
  <div :class="{ container: !noContainer }">
    <label v-if="label.length > 0" :for="inputId">
      {{ label }}
      <span v-if="charLimit" class="char-length">
        {{ countChar }} / {{ charLimit }}
      </span>

      <span v-if="optional" class="optional"> ({{ $t('OPTIONAL') }}) </span>
    </label>
    <input
      :id="inputId"
      v-model="inputValue"
      :class="{ error: isError }"
      :placeholder="placeholder"
      :disabled="disabled"
      :name="name"
      :maxlength="charLimit"
      :type="type"
      :data-cy="dataCy"
      :autocomplete="autocompleteMode"
      :list="listName"
      :min="min"
      @change="handleChange"
      @click="handleClick"
      @focus="handleFocus"
      @blur="handleBlur"
      @keyup="onKeyUp"
    />
    <div v-if="validate" class="validation-container">
      <div v-if="isError" class="validation-icon">
        <img src="@/assets/img/error_x.svg" />
      </div>
    </div>
    <FieldTip v-if="isError" :message="errorMessage" class="warning" />
  </div>
</template>

<script>
/**
 * @deprecated - use BaseTextInput component instead
 */
import FieldTip from '@/components/generics/form/FieldTip.vue'
import { nanoid } from 'nanoid'

export default {
  components: {
    FieldTip,
  },
  props: {
    label: {
      type: String,
      required: false,
      default: '',
    },
    placeholder: {
      type: String,
      required: true,
    },
    type: {
      type: String,
      default: 'text',
    },
    optional: {
      type: Boolean,
      default: false,
    },
    value: {
      type: [Number, String],
      required: false,
      default: '',
    },
    name: {
      type: String,
      required: true,
    },
    errorMessage: {
      type: String,
      default: null,
    },
    validate: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    charLimit: {
      type: Number,
      required: false,
      default: null,
    },
    dataCy: {
      type: String,
      required: false,
      default: '',
    },
    listName: {
      type: String,
      required: false,
      default: null,
    },
    min: {
      type: Number,
      required: false,
      default: null,
    },
    id: {
      type: String,
      required: false,
      default: null,
    },
    noContainer: {
      type: Boolean,
      required: false,
      default: false,
    },
    autocompleteDisabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ['changeValue', 'inputBlurred', 'inputFocused', 'onKeyUp', 'onClick'],
  data() {
    return {
      inputValue: this.value,
      edited: false,
      focus: false,
    }
  },
  computed: {
    inputId() {
      return this.id ?? nanoid()
    },
    isError() {
      return this.errorMessage !== null && this.errorMessage.length > 0
    },
    countChar() {
      return this.inputValue.length
    },
    autocompleteMode() {
      return this.autocompleteDisabled ? 'nope' : 'off' // HACK: as per https://stackoverflow.com/questions/12374442/chrome-ignores-autocomplete-off, hack applied to make Chrome actually respect no autocomletion
    },
  },
  watch: {
    value: function (value) {
      this.inputValue = value
    },
  },
  methods: {
    handleChange() {
      this.edited = true
      this.$emit('changeValue', {
        name: this.name,
        value: this.inputValue,
        error: this.isError,
      })
    },
    handleBlur(event) {
      this.focus = false
      this.$emit('inputBlurred', event)
    },
    handleClick() {
      this.$emit('onClick')
    },
    handleFocus() {
      this.focus = true
      this.$emit('inputFocused')
    },
    onKeyUp(event) {
      this.$emit('onKeyUp', { value: this.inputValue, key: event.keyCode })
    },
  },
}
</script>

<style lang="scss" scoped>
.container {
  margin-bottom: 20px;

  &.large input {
    padding: $formElementsPaddingLarge;
    font-size: $formElementsFontSizeLarge;
  }

  label {
    @include formLabelStyle;
    display: block;
    position: relative;

    .char-length {
      color: $color-text-light;
      position: absolute;
      right: 2%;
    }
    .optional {
      color: $color-text-light;
      display: inline;
      font-size: 12px;
      font-weight: 900;
      margin-left: $spacing-5xs;
    }
  }

  input {
    -moz-appearance: none;
    -webkit-appearance: none;
    appearance: none;
    background: $formElementsBackground;
    border-radius: $formElementsBorderRadius;
    border: $formElementsBorder;
    color: $color-text;
    display: inline-block;
    font-size: $formElementsFontSize;
    font-weight: $formElementsFontWeight;
    padding: $formElementsPadding;
    width: 100%;
    line-height: $spacing-xs;

    &:disabled {
      -webkit-opacity: 1; /* Override iOS opacity change affecting text & background color */
      -webkit-text-fill-color: $color-text; /* Override iOS / Android font color change */
      background: $color-disabled;
      color: $color-text; /* Override IE font color change */
      opacity: 1;
    }
    &::placeholder {
      color: $formElementsPlaceholderColor;
      font-weight: $formElementsFontWeight;
    }
    &:focus {
      border: 1px solid $color-navigation;
      outline: none;
    }
    &.error {
      border: 1px solid $color-error;
      outline-color: $color-error;
    }
    &.success {
      border: 1px solid $color-green-40;
    }
  }

  .validation-container {
    display: inline-block;
    margin-left: -30px;

    .validation-icon img {
      vertical-align: middle;
    }
  }
}

input:-moz-placeholder,
input::-moz-placeholder {
  opacity: 1;
}
</style>
